// Hover-to-preview for a video card, shared by Explore and the Workspace.
//
// Lifted out of ExplorePage's VideoCard rather than copied into the Workspace: two inline
// copies of the same behaviour is how the mode picker ended up meaning different things on
// two pages. The delay and the mount-on-hover rule below are the whole point of this
// component, and they are exactly the parts a second copy would quietly lose.
import React, { useCallback, useEffect, useRef, useState } from "react";

// Don't fetch video for a cursor merely passing through. A sweep across a row of cards
// should cost nothing; only a deliberate rest on one should spend 3 MB.
export const HOVER_PREVIEW_DELAY_MS = 500;

/**
 * Returns hover handlers plus the <video> to render (or null).
 *
 * The element is mounted ONLY while hovered. Rendering it always — even paused, even with
 * preload="none" — would put 20+ video elements per row on the page and let the browser
 * decide to start fetching them. Mounting on hover means exactly one is ever loading.
 *
 * @param downloadUrl the video's mp4; falsy disables the preview entirely
 * @param className   positioning for the <video>, so each page can place it in its own stack
 */
export function useHoverPreview(downloadUrl, className) {
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const [previewing, setPreviewing] = useState(false);
  const [ready, setReady] = useState(false);

  const onMouseEnter = useCallback(() => {
    if (!downloadUrl) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setPreviewing(true), HOVER_PREVIEW_DELAY_MS);
  }, [downloadUrl]);

  const onMouseLeave = useCallback(() => {
    clearTimeout(timerRef.current);
    setPreviewing(false);
    setReady(false);
  }, []);

  // The timer must not outlive the card — rows unmount on search and on the Workspace's
  // per-row windowing, and a pending timer would set state on a dead component and start a
  // download nobody is looking at.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const preview = previewing ? (
    <video
      ref={videoRef}
      src={downloadUrl}
      muted
      autoPlay
      playsInline
      preload="none"
      // Faded in on canplay rather than shown immediately: swapping a black video element
      // over the thumbnail the instant it mounts reads as the card breaking.
      onCanPlay={() => {
        setReady(true);
        videoRef.current?.play().catch(() => {});
      }}
      className={className}
      style={{ opacity: ready ? 1 : 0 }}
    />
  ) : null;

  return { hoverProps: { onMouseEnter, onMouseLeave }, preview, previewing, ready };
}
